import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import SubscribeForm from "@/components/feature/SubscribeForm";
import { useDocumentMeta, SITE_ORIGIN } from "@/hooks/useDocumentMeta";
import { communityTestimonials, contentPrinciples, siteInfo, siteMilestones } from "@/mocks/site";
import { communityFaq } from "@/mocks/faq";
import { communityPosts } from "@/mocks/posts";
import { formatCount } from "@/utils/format";
import AboutFaq from "./components/AboutFaq";

const anchors = [
  { id: "intro", label: "品牌介绍" },
  { id: "principles", label: "内容原则" },
  { id: "milestones", label: "发展历程" },
  { id: "voices", label: "用户口碑" },
  { id: "faq", label: "常见问题" },
  { id: "contact", label: "联系我们" },
];

export default function About() {
  useDocumentMeta({
    title: "关于黑料网社 - 内容原则、发展历程与联系方式",
    description:
      "黑料网社是一个轻松向的网络吃瓜社区。了解我们的品牌定位、内容原则、发展历程与用户口碑，以及投稿与联系方式。热帖专区与高赞神回复，从这里开始。",
    keywords: "关于黑料网社,黑料网社介绍,内容原则,吃瓜社区,神回复社区",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于黑料网社",
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        slogan: siteInfo.slogan,
        email: siteInfo.email,
        foundingDate: String(siteInfo.since),
        url: SITE_ORIGIN,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
        ],
      },
    ],
  });

  const totalHeat = communityPosts.reduce((sum, post) => sum + post.heat, 0);

  return (
    <SiteLayout>
      <header className="w-full bg-primary-50 border-b border-primary-200 pt-24 md:pt-28 pb-10 md:pb-14">
        <div className="max-w-content mx-auto px-4 md:px-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
            关于{siteInfo.name}
          </h1>
          <p className="mt-4 text-sm md:text-lg text-foreground-700 leading-relaxed max-w-3xl">
            {siteInfo.slogan}。我们相信，把日常里那些好笑、温暖、值得会心一笑的瞬间认真记录下来，
            本身就是一件有意义的事。
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/hot"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium bg-foreground-950 text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-fire-line"></i>
              去逛热帖榜
            </Link>
            <a
              href={`mailto:${siteInfo.email}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium bg-background-50 text-foreground-800 border border-primary-200 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              投稿与合作
            </a>
          </div>
        </div>
      </header>

      <div className="w-full bg-background-50">
        <div className="max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <nav className="lg:col-span-3" aria-label="页面目录">
              <div className="lg:sticky lg:top-24">
                <p className="text-xs tracking-[0.24em] uppercase text-foreground-400 mb-4">
                  页面目录
                </p>
                <ul className="flex flex-wrap lg:flex-col gap-2">
                  {anchors.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="block px-4 py-2.5 rounded-md text-sm text-foreground-700 bg-background-100 border border-background-200 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>

            <div className="lg:col-span-9 space-y-14 md:space-y-16">
              <section id="intro" className="scroll-mt-24">
                <SectionHeading kicker="Who We Are" title="我们是谁，我们在做什么" />
                <div className="mt-6 space-y-5 text-sm md:text-base text-foreground-700 leading-[1.9]">
                  <p>{siteInfo.intro}</p>
                  <p>
                    我们不做高高在上的评论，也不追求耸动的标题。黑料网社的内容来自社区投稿与日常记录，
                    用朴素的叙述把一件事讲清楚，再让评论区接上最好玩的那半段。
                  </p>
                  <p>
                    从 2019 年第一条帖子算起，社区已经积累了大量内容：目前站内演示板块的帖子累计热度达到{" "}
                    <strong className="text-foreground-950">{formatCount(totalHeat)}</strong>
                    ，被加精的神回复也在持续增加。这些数字背后，是一个个愿意认真写两句话的普通人。
                  </p>
                </div>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: "内容定位", value: "轻松向吃瓜社区", icon: "ri-leaf-line" },
                    { label: "核心板块", value: "热帖 · 神回复 · 加精", icon: "ri-layout-masonry-line" },
                    { label: "阅读方式", value: "每天刷一遍热帖榜", icon: "ri-smartphone-line" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="p-5 rounded-lg bg-background-100 border border-background-200"
                    >
                      <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <p className="mt-4 text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-1 text-sm font-medium text-foreground-900">{item.value}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="principles" className="scroll-mt-24">
                <SectionHeading
                  kicker="Our Principles"
                  title="内容原则"
                  desc="热闹归热闹，该守的底线一条都不少。以下四条，是我们筛选与呈现内容时一直坚持的东西。"
                />
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {contentPrinciples.map((principle, index) => (
                    <div
                      key={principle.title}
                      className="flex gap-4 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200"
                    >
                      <span className="w-11 h-11 shrink-0 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                        <i className={`${principle.icon} text-xl`}></i>
                      </span>
                      <div>
                        <h3 className="font-heading text-base font-bold text-foreground-950">
                          {String(index + 1).padStart(2, "0")} · {principle.title}
                        </h3>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                          {principle.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section id="milestones" className="scroll-mt-24">
                <SectionHeading
                  kicker="Milestones"
                  title="发展历程"
                  desc="从一个小论坛到一个稳定的社区，这中间没有捷径，只有一条条被顶上去的帖子。"
                />
                <ol className="mt-8 relative border-l-2 border-background-200 ml-3 space-y-8">
                  {siteMilestones.map((milestone) => (
                    <li key={milestone.year} className="pl-6 md:pl-8 relative">
                      <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50"></span>
                      <span className="inline-block px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                        {milestone.year}
                      </span>
                      <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">
                        {milestone.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-2xl">
                        {milestone.desc}
                      </p>
                    </li>
                  ))}
                </ol>
              </section>

              <section id="voices" className="scroll-mt-24">
                <SectionHeading
                  kicker="Reader Voices"
                  title="用户口碑"
                  desc="与其自夸，不如听听每天来刷热帖榜的人怎么说。"
                />
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {communityTestimonials.map((item) => (
                    <figure
                      key={item.name}
                      className="flex flex-col p-6 rounded-lg bg-background-100 border border-background-200"
                    >
                      <blockquote className="text-sm text-foreground-700 leading-relaxed flex-1">
                        「{item.text}」
                      </blockquote>
                      <figcaption className="mt-5 flex items-center gap-3">
                        <span className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-heading font-bold">
                          {item.name.slice(0, 1)}
                        </span>
                        <span>
                          <span className="block text-sm font-medium text-foreground-900">
                            {item.name}
                          </span>
                          <span className="block text-xs text-foreground-500">{item.role}</span>
                        </span>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </section>

              <section id="faq" className="scroll-mt-24">
                <SectionHeading kicker="FAQ" title="常见问题" />
                <div className="mt-6">
                  <AboutFaq items={communityFaq} />
                </div>
              </section>

              <section id="contact" className="scroll-mt-24">
                <SectionHeading
                  kicker="Contact"
                  title="联系我们"
                  desc="投稿、内容反馈、合作洽谈，都可以通过下面的方式找到我们。"
                />
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  <div className="p-6 rounded-lg bg-background-100 border border-background-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      邮件联系
                    </h3>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      无论是投稿还是反馈，请发送邮件至下方地址，注明分类与正文。
                      我们会在三个工作日内回复。
                    </p>
                    <a
                      href={`mailto:${siteInfo.email}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors cursor-pointer"
                    >
                      <i className="ri-mail-send-line"></i>
                      {siteInfo.email}
                    </a>
                    <ul className="mt-5 space-y-2 text-sm text-foreground-600">
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-secondary-600"></i>
                        投稿请附上分类与正文草稿
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-secondary-600"></i>
                        内容反馈请在标题注明「反馈」
                      </li>
                    </ul>
                  </div>
                  <div className="p-6 rounded-lg bg-primary-50 border border-primary-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      订阅吃瓜日报
                    </h3>
                    <p className="mt-3 text-sm text-foreground-700 leading-relaxed mb-4">
                      不想每天自己刷？留下邮箱，我们把当日热帖与一句神回复直接送到你手里。
                    </p>
                    <SubscribeForm variant="inline" />
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}